<template>
  <div class="mx-2 mx-md-0">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <b-button variant="success" size="sm" @click="refreshData">
          <b-icon-arrow-clockwise class="mr-1"></b-icon-arrow-clockwise>
          REFRESH
        </b-button>
      </div>
    </div>

    <!-- Filters -->
    <div style="text-align: left; margin-top: 5px; margin-bottom: 15px;">
      <b-card>
        <!-- Filter by Location -->
        <b-alert variant="success" show style="display: inline-block; width: 100%; max-width: 300px; margin: 5px;">
          <div class="mb-1">
            FILTER BY LOCATION
          </div>
          <b-form-select
            v-model="selectedLocation"
            :options="locationOptions"
            @change="filterProducts"
            size="sm"
            style="width: 100%;"
          >
            <option :value="null">ALL LOCATIONS</option>
          </b-form-select>
        </b-alert>

        <!-- Filter by Category -->
        <b-alert variant="success" show style="display: inline-block; width: 100%; max-width: 300px; margin: 5px;">
          <div class="mb-1">
            FILTER BY CATEGORY
          </div>
          <b-form-select
            v-model="selectedCategory"
            :options="categoryOptions"
            @change="filterProducts"
            size="sm"
            style="width: 100%;"
          >
            <option :value="null">ALL CATEGORIES</option>
          </b-form-select>
        </b-alert>

        <!-- Filter by Vendor -->
        <b-alert variant="success" show style="display: inline-block; width: 100%; max-width: 300px; margin: 5px;">
          <div class="mb-1">
            FILTER BY VENDOR
          </div>
          <b-form-select
            v-model="selectedVendor"
            :options="vendorOptions"
            @change="filterProducts"
            size="sm"
            style="width: 100%;"
          >
            <option :value="null">ALL VENDORS</option>
          </b-form-select>
        </b-alert>

        <!-- Search by SKU or Name -->
        <b-alert variant="info" show style="display: inline-block; width: 100%; max-width: 350px; margin: 5px;">
          <div class="mb-1">
            SEARCH BY SKU OR NAME
          </div>
          <b-input
            v-model="searchQuery"
            placeholder="Enter SKU or product name..."
            size="sm"
            @input="filterProducts"
            style="width: 100%;"
          ></b-input>
        </b-alert>

        <!-- Filter Options -->
        <!-- <b-alert variant="info" show style="display: inline-block; width: 100%; max-width: 350px; margin: 5px;">
          <div class="mb-1">
            FILTER OPTIONS
          </div>
          <div>
            <b-form-checkbox
              v-model="showAllLowStock"
              @change="filterProducts"
              size="sm"
              style="font-size: 14px;"
            >
              Show all low stock (< 5) even without notification
            </b-form-checkbox>
          </div>
        </b-alert> -->
      </b-card>
    </div>

    <!-- Main Table -->
    <b-card>
      <div v-if="loading">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <span class="ml-2">Loading low inventory alerts...</span>
      </div>

      <div v-else-if="filteredProducts.length === 0">
        <b-alert variant="success" show class="text-center">
          <b-icon-check-circle-fill class="mr-2"></b-icon-check-circle-fill>
          No low inventory alerts at the moment! All products have sufficient stock.
        </b-alert>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-striped table-hover low-inventory-table">
            <thead>
              <tr>
                <th @click="sortBy('product_sku')" class="sortable">
                  SKU
                  <b-icon-chevron-up v-if="sortField === 'product_sku' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'product_sku' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <th @click="sortBy('product_name')" class="sortable">
                  PRODUCT NAME
                  <b-icon-chevron-up v-if="sortField === 'product_name' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'product_name' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <th @click="sortBy('category')" class="sortable">
                  CATEGORY
                  <b-icon-chevron-up v-if="sortField === 'category' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'category' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <th @click="sortBy('location_name')" class="sortable">
                  LOCATION
                  <b-icon-chevron-up v-if="sortField === 'location_name' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'location_name' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <th @click="sortBy('in_stock')" class="sortable">
                  IN STOCK
                  <b-icon-chevron-up v-if="sortField === 'in_stock' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'in_stock' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <th @click="sortBy('available')" class="sortable">
                  AVAILABLE
                  <b-icon-chevron-up v-if="sortField === 'available' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'available' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <th @click="sortBy('reserved')" class="sortable">
                  RESERVED
                  <b-icon-chevron-up v-if="sortField === 'reserved' && sortDirection === 'asc'"></b-icon-chevron-up>
                  <b-icon-chevron-down v-if="sortField === 'reserved' && sortDirection === 'desc'"></b-icon-chevron-down>
                </th>
                <!-- <th>ACTIONS</th> -->
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in filteredProducts" :key="product.locsub">
                <td>
                  <strong>{{ product.product_sku }}</strong>
                  <b-badge
                    v-if="product.properties && product.properties.low_inventory"
                    variant="info"
                    pill
                    class="ml-2"
                    v-b-tooltip.hover
                    title="Low inventory notification enabled"
                  >
                    <b-icon-bell-fill></b-icon-bell-fill>
                  </b-badge>
                </td>
                <td>
                  <div class="font-weight-bold">{{ product.product_name }}</div>
                  <small class="text-muted">
                    <b-icon-tag-fill class="mr-1"></b-icon-tag-fill>
                    {{ product.category }}
                  </small>
                </td>
                <td>{{ product.category }}</td>
                <td>
                  <div>{{ product.location_name }}</div>
                  <small class="text-muted">{{ product.sublocation_name }}</small>
                </td>
                <td>
                   <span :class="{'text-danger font-weight-bold': product.available <= 0}">
                    {{ product.in_stock }}
                  </span>
                </td>
                <td>
                  <span :class="{'text-danger font-weight-bold': product.available <= 0}">
                    {{ product.available }}
                  </span>
                </td>
                <td>{{ product.reserved }}</td>
                <!-- <td>
                  <b-button-group size="sm">
                    <b-button
                      variant="outline-primary"
                      @click="viewProductDetails(product)"
                      v-b-tooltip.hover
                      title="View Details"
                    >
                      <b-icon-eye></b-icon-eye>
                    </b-button>
                    <b-button
                      variant="outline-success"
                      @click="restockProduct(product)"
                      v-b-tooltip.hover
                      title="Restock"
                    >
                      <b-icon-truck></b-icon-truck>
                    </b-button>
                    <b-button
                      variant="outline-warning"
                      @click="openInventoryPage(product)"
                      v-b-tooltip.hover
                      title="View in Inventory"
                    >
                      <b-icon-box-arrow-up-right></b-icon-box-arrow-up-right>
                    </b-button>
                  </b-button-group>
                </td> -->
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>

    <!-- Restock Modal -->
    <b-modal
      id="restock-modal"
      :title="'Restock Product - ' + (selectedProduct ? selectedProduct.product_sku : '')"
      @ok="submitRestock"
      @hidden="resetRestockForm"
    >
      <div v-if="selectedProduct">
        <b-card class="mb-3">
          <b-row>
            <b-col>
              <strong>Product:</strong> {{ selectedProduct.product_name }}
            </b-col>
            <b-col>
              <strong>Current Stock:</strong> {{ selectedProduct.in_stock }}
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col>
              <strong>Location:</strong> {{ selectedProduct.location_name }}
            </b-col>
            <b-col>
              <strong>Sublocation:</strong> {{ selectedProduct.sublocation_name }}
            </b-col>
          </b-row>
        </b-card>

        <b-form @submit.prevent="submitRestock">
          <b-form-group label="Restock Quantity" label-for="restock-quantity">
            <b-form-input
              id="restock-quantity"
              v-model="restockQuantity"
              type="number"
              min="1"
              max="1000"
              required
              placeholder="Enter quantity to add"
            ></b-form-input>
            <small class="text-muted">Note: This will increase the "Received" count for this product.</small>
          </b-form-group>

          <b-form-group label="Source" label-for="restock-source">
            <b-form-select
              id="restock-source"
              v-model="restockSource"
              :options="restockSources"
              required
            ></b-form-select>
          </b-form-group>

          <b-form-group label="Notes" label-for="restock-notes">
            <b-form-textarea
              id="restock-notes"
              v-model="restockNotes"
              placeholder="Add any notes about this restock..."
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: "LowInventory",
  
  components: {
    Loading
  },

  data() {
    return {
      loading: true,
      products: [],
      filteredProducts: [],
      selectedProduct: null,
      
      // Filters
      selectedLocation: null,
      selectedCategory: null,
      selectedVendor: null,
      searchQuery: '',
      showAllLowStock: false,
      
      // Sorting
      sortField: 'in_stock',
      sortDirection: 'asc',
      
      // Restock form
      restockQuantity: 1,
      restockSource: 'vendor_order',
      restockNotes: '',
      restockSources: [
        { value: 'vendor_order', text: 'Vendor Order' },
        { value: 'transfer', text: 'Transfer from Another Location' },
        { value: 'return', text: 'Customer Return' },
        { value: 'found', text: 'Found in Warehouse' },
        { value: 'other', text: 'Other' }
      ]
    };
  },

  computed: {
    locationOptions() {
      const locations = [...new Set(this.products.map(p => p.location_name).filter(Boolean))];
      return locations.map(loc => ({ value: loc, text: loc }));
    },
    
    categoryOptions() {
      const categories = [...new Set(this.products.map(p => p.category).filter(Boolean))];
      return categories.map(cat => ({ value: cat, text: cat }));
    },
    
    vendorOptions() {
      const vendors = [...new Set(this.products.map(p => p.vendor).filter(Boolean))];
      return vendors.map(vendor => ({ value: vendor, text: vendor }));
    },
    
    criticalCount() {
      return this.filteredProducts.filter(p => p.in_stock <= 1).length;
    },
    
    lowStockCount() {
      return this.filteredProducts.filter(p => p.in_stock > 1 && p.in_stock < 5).length;
    },
    
    notificationCount() {
      return this.filteredProducts.filter(p => 
        p.properties && p.properties.low_inventory
      ).length;
    }
  },

  methods: {
    async refreshData() {
      await this.loadLowInventoryProducts();
    },

    async loadLowInventoryProducts() {
      this.loading = true;
      try {
        const path = "/api/product/get_low_inventory/";
        const response = await Requestor.getAsync(path, {});
        
        if (response.data.error) {
          this.$bvToast.toast(
            response.data.error,
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
          this.products = [];
        } else {
          this.products = response.data.result || [];
          this.filterProducts();
        }
      } catch (error) {
        console.error("Error loading low inventory:", error);
        this.$bvToast.toast(
          "Error loading low inventory data",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        this.products = [];
      } finally {
        this.loading = false;
      }
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      
      this.filteredProducts.sort((a, b) => {
        let aValue = a[field];
        let bValue = b[field];
        
        // Handle numeric sorting for stock fields
        if (field === 'in_stock' || field === 'available' || field === 'reserved') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // Handle string sorting
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },

    filterProducts() {
      let filtered = this.products;
      
      // Apply low stock filter
      if (!this.showAllLowStock) {
        filtered = filtered.filter(p => 
          p.properties && p.properties.low_inventory === true
        );
      }
      
      // Apply location filter
      if (this.selectedLocation) {
        filtered = filtered.filter(p => p.location_name === this.selectedLocation);
      }
      
      // Apply category filter
      if (this.selectedCategory) {
        filtered = filtered.filter(p => p.category === this.selectedCategory);
      }
      
      // Apply vendor filter
      if (this.selectedVendor) {
        filtered = filtered.filter(p => p.vendor === this.selectedVendor);
      }
      
      // Apply search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(p => 
          (p.product_sku && p.product_sku.toLowerCase().includes(query)) ||
          (p.product_name && p.product_name.toLowerCase().includes(query))
        );
      }
      
      // Ensure stock is < 5
      filtered = filtered.filter(p => p.in_stock < 5);
      
      // Sort by stock level (lowest first)
      filtered.sort((a, b) => a.in_stock - b.in_stock);
      
      this.filteredProducts = filtered;
    },

    getStockBadgeVariant(stock) {
      if (stock === 0) return 'danger';
      if (stock <= 1) return 'warning';
      return 'info';
    },

    getStockIcon(stock) {
      if (stock === 0) return 'exclamation-triangle';
      if (stock <= 1) return 'exclamation-circle';
      return 'info-circle';
    },

    viewProductDetails(product) {
      this.selectedProduct = product;
      this.$bvModal.show('restock-modal');
    },

    restockProduct(product) {
      this.selectedProduct = product;
      this.restockQuantity = Math.max(1, 5 - product.in_stock);
      this.$bvModal.show('restock-modal');
    },

    async submitRestock() {
      if (!this.selectedProduct || !this.restockQuantity) {
        this.$bvToast.toast("Please enter a valid restock quantity", {
          title: "Error",
          autoHideDelay: 1500,
          variant: "warning"
        });
        return;
      }

      try {
        const path = "/api/inventory/update_inventory_field/";
        const splitted = this.selectedProduct.locsub.split("|");
        const location_id = parseInt(splitted[0]);
        const sublocation_id = parseInt(splitted[1]);

        const data = {
          product_sku: this.selectedProduct.product_sku,
          location_id: location_id,
          sublocation_id: sublocation_id,
          field: 'received',
          value: parseInt(this.restockQuantity),
          source: this.restockSource,
          notes: this.restockNotes,
          username: this.getUsername()
        };

        const response = await Requestor.postAsync(path, data);
        
        if (response.data.error) {
          this.$bvToast.toast(response.data.error, {
            title: "Error",
            autoHideDelay: 1500,
            variant: "warning"
          });
        } else {
          this.$bvToast.toast("Successfully restocked product!", {
            title: "Success",
            autoHideDelay: 1500,
            variant: "success"
          });
          this.$bvModal.hide('restock-modal');
          this.resetRestockForm();
          await this.refreshData();
        }
      } catch (error) {
        console.error("Error restocking:", error);
        this.$bvToast.toast("Error restocking product", {
          title: "Error",
          autoHideDelay: 1500,
          variant: "danger"
        });
      }
    },

    resetRestockForm() {
      this.restockQuantity = 1;
      this.restockSource = 'vendor_order';
      this.restockNotes = '';
      this.selectedProduct = null;
    },

    openInventoryPage(product) {
      this.$router.push({
        path: '/inventory',
        query: {
          location_id: product.location_id,
          product_sku: product.product_sku
        }
      });
    },

    getUsername() {
      try {
        const userDetails = JSON.parse(localStorage.userDetails || "{}");
        return userDetails.username || 'admin';
      } catch (err) {
        return 'admin';
      }
    }
  },

  mounted() {
    this.loadLowInventoryProducts();
  }
};
</script>

<style scoped>
.low-inventory-table {
  font-size: 0.9rem;
}

.low-inventory-table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #2c3e50;
}

.low-inventory-table tbody tr:hover {
  background-color: #f5f5f5;
}

.stock-badge {
  font-size: 0.85rem;
  padding: 4px 8px;
  min-width: 60px;
  display: inline-block;
  text-align: center;
}

.product-name-cell {
  max-width: 300px;
  word-wrap: break-word;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .low-inventory-table {
    font-size: 0.8rem;
  }
  
  .stock-badge {
    font-size: 0.75rem;
    padding: 2px 6px;
    min-width: 50px;
  }
  
  .product-name-cell {
    max-width: 200px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Products/LowInventory.vue